import { createSlice } from "@reduxjs/toolkit";
import isNullish from "utils/isNullish";

import { groundedSourcesOf, groundingFromSelection } from "./grounding";
import {
  deleteWriterConversation,
  fetchWriterConversations,
  openWriterConversation,
  readWriterConversation,
  renameWriterConversation,
} from "./thunks";
import {
  isFirstSaveOf,
  isSameSubject,
  isUnsavedDocument,
  WRITER_SURFACE,
  writerSubjectOfRow,
} from "./writerSubject";

export const WriterView = Object.freeze({
  CHAT: "chat",
  HISTORY: "history",
  READING: "reading",
});

export const WriterStorage = Object.freeze({
  SAVED: "SAVED",
  UNAVAILABLE: "UNAVAILABLE",
});

export const INITIAL_STATE = {
  subject: null,
  conversationKey: null,
  conversationId: null,
  title: null,
  promoteFromNew: false,
  pendingFirstSave: null,
  exchanges: [],
  epoch: 0,
  requestGeneration: 0,
  isUnavailable: false,
  view: WriterView.CHAT,
  conversations: [],
  isLoadingConversations: false,
  isListingAllPages: false,
  areListedConversationsFromAllPages: false,
  conversationsError: null,
  isNaming: false,
  isOpening: false,
  openRequestId: null,
  listRequestId: null,
  renames: {},
  reading: null,
  isOpeningReading: false,
  readRequestId: null,
  readTargetId: null,
  grounding: null,
  composerDocuments: [],
  composerCollections: [],
};

const chipsOf = (selection) =>
  (selection?.documents ?? [])
    .filter((document) => document.hash)
    .map((document) => ({
      id: document.documentId,
      name: document.label,
      hash: document.hash,
    }));

const collectionChipsOf = (selection) =>
  (selection?.collections ?? []).map((collection) => ({
    id: collection.collectionId,
    name: collection.label,
    files: Array.from({ length: collection.documentCount ?? 0 }, () => ({})),
  }));

const newKey = () => crypto.randomUUID();

const startFresh = (state, conversationKey) => {
  state.conversationKey = conversationKey;
  state.conversationId = null;
  state.title = null;
  state.promoteFromNew = false;
  state.exchanges = [];
  state.isUnavailable = false;
  state.isNaming = false;
  state.isOpening = false;
  state.openRequestId = null;
  state.grounding = null;
  state.composerDocuments = [];
  state.composerCollections = [];
  state.epoch += 1;
  state.requestGeneration += 1;
};

const showTitle = (state, conversationId, title) => {
  if (conversationId === state.conversationId) {
    state.title = title;
  }
  state.conversations.forEach((conversation) => {
    if (conversation.id === conversationId) {
      conversation.title = title;
    }
  });
  if (state.reading?.id === conversationId) {
    state.reading.title = title;
  }
};

const settleRename = (state, conversationId) => {
  const rename = state.renames[conversationId];
  rename.pending -= 1;
  if (rename.pending === 0) {
    delete state.renames[conversationId];
  }
};

const stopListing = (state) => {
  state.listRequestId = null;
  state.isLoadingConversations = false;
  state.isListingAllPages = false;
  state.areListedConversationsFromAllPages = false;
};

const stopReading = (state) => {
  state.reading = null;
  state.isOpeningReading = false;
  state.readRequestId = null;
  state.readTargetId = null;
};

const readOnlyTurn = (turn) => ({
  question: turn.question ?? "",
  answer: turn.answer ?? "",
});

const readingOf = (row, detail) => {
  const isWriter = row.surface === WRITER_SURFACE;
  return {
    id: row.id,
    surface: row.surface,
    title: detail.title ?? row.title ?? null,
    firstPrompt: row.firstPrompt ?? null,
    location: row,
    exchanges: isWriter ? (detail.turns ?? []).map(storedExchange) : [],
    turns: isWriter ? [] : (detail.turns ?? []).map(readOnlyTurn),
  };
};

const isActiveRequest = (state, { generation, conversationKey }) =>
  generation === state.requestGeneration &&
  conversationKey === state.conversationKey;

export const storedExchange = (turn) => ({
  ref: turn.turnRef,
  question: turn.question ?? "",
  answer: turn.answer ?? "",
  citations: (turn.citations ?? []).map((citation) => ({
    citationNumber: citation.citationNumber,
    documentId: citation.documentId ?? null,
    documentName: citation.documentName ?? null,
    sourceKind: citation.sourceKind ?? null,
    collectionId: citation.collectionId ?? null,
    collectionName: citation.collectionName ?? null,
    availability: citation.availability ?? null,
  })),
  clarificationAnswers: turn.clarificationAnswers ?? [],
  ...groundedSourcesOf(turn.sources),
});

const writerSlice = createSlice({
  name: "visiWriter",
  initialState: INITIAL_STATE,
  reducers: {
    writerDocumentOpened: {
      reducer: (state, { payload }) => {
        if (isFirstSaveOf(state.pendingFirstSave, payload.subject)) {
          state.subject = payload.subject;
          state.promoteFromNew = true;
          state.pendingFirstSave = null;
          return;
        }
        state.subject = payload.subject;
        state.pendingFirstSave = null;
        state.view = WriterView.CHAT;
        state.conversations = [];
        stopListing(state);
        stopReading(state);
        startFresh(state, payload.conversationKey);
      },
      prepare: (subject) => ({
        payload: { subject, conversationKey: newKey() },
      }),
    },
    writerDocumentClosed: (state) => {
      stopReading(state);
      state.subject = null;
      state.openRequestId = null;
      stopListing(state);
      state.isOpening = false;
      state.requestGeneration += 1;
    },
    writerChatStarted: {
      reducer: (state, { payload }) => {
        state.view = WriterView.CHAT;
        stopReading(state);
        startFresh(state, payload.conversationKey);
      },
      prepare: () => ({ payload: { conversationKey: newKey() } }),
    },
    writerDocumentFirstSaved: (state, { payload }) => {
      if (!isUnsavedDocument(state.subject)) {
        return;
      }
      state.pendingFirstSave = {
        from: state.subject,
        documentId: String(payload.documentId),
      };
    },
    writerRequestStarted: (state) => {
      state.requestGeneration += 1;
      state.composerDocuments = [];
      state.composerCollections = [];
    },
    writerGroundingChanged: (state, { payload }) => {
      if (payload.conversationKey === state.conversationKey) {
        state.grounding = payload.grounding ?? null;
      }
    },
    writerComposerChanged: (state, { payload }) => {
      if (payload.conversationKey !== state.conversationKey) {
        return;
      }
      if (payload.documents) {
        state.composerDocuments = payload.documents;
      }
      if (payload.collections) {
        state.composerCollections = payload.collections;
      }
    },
    writerRequestAbandoned: (state) => {
      state.requestGeneration += 1;
    },
    writerResultReceived: (state, { payload }) => {
      if (!isActiveRequest(state, payload)) {
        return;
      }
      const { result } = payload;
      if (!isNullish(result?.conversationId)) {
        state.conversationId = result.conversationId;
      }
      if (result?.storage === WriterStorage.SAVED && payload.promotedFromNew) {
        state.promoteFromNew = false;
      }
      if (result?.storage === WriterStorage.UNAVAILABLE) {
        state.isUnavailable = true;
      }
    },
    writerConversationRefused: (state, { payload }) => {
      if (isActiveRequest(state, payload)) {
        state.isUnavailable = true;
      }
    },
    writerExchangeCompleted: (state, { payload }) => {
      if (
        payload.conversationKey !== state.conversationKey ||
        payload.epoch !== state.epoch
      ) {
        return;
      }
      state.exchanges.push(payload.exchange);
    },
    writerHistoryShown: (state) => {
      if (state.view === WriterView.HISTORY) {
        return;
      }
      stopReading(state);
      state.view = WriterView.HISTORY;
      state.conversations = [];
      state.conversationsError = null;
      stopListing(state);
      state.isLoadingConversations = true;
    },
    writerChatShown: (state) => {
      state.view = WriterView.CHAT;
      state.openRequestId = null;
      state.isOpening = false;
      stopReading(state);
    },
    writerNamingStarted: (state, { payload }) => {
      if (payload.conversationKey === state.conversationKey) {
        state.isNaming = true;
      }
    },
    writerNamingStopped: (state, { payload }) => {
      if (payload.conversationKey === state.conversationKey) {
        state.isNaming = false;
      }
    },
    writerConversationNamed: (state, { payload }) => {
      if (payload.conversationId === state.conversationId) {
        state.title = payload.title;
        state.isNaming = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWriterConversations.pending, (state, { meta }) => {
        state.isLoadingConversations = true;
        state.conversationsError = null;
        state.listRequestId = meta.requestId;
        state.isListingAllPages = Boolean(meta.arg?.allPages);
      })
      .addCase(
        fetchWriterConversations.fulfilled,
        (state, { meta, payload }) => {
          if (meta.requestId !== state.listRequestId) {
            return;
          }
          state.conversations = payload;
          state.isLoadingConversations = false;
          state.listRequestId = null;
          state.isListingAllPages = Boolean(meta.arg?.allPages);
          state.areListedConversationsFromAllPages = Boolean(
            meta.arg?.allPages
          );
        }
      )
      .addCase(
        fetchWriterConversations.rejected,
        (state, { meta, payload }) => {
          if (meta.requestId === state.listRequestId) {
            state.isLoadingConversations = false;
            state.listRequestId = null;
            state.conversations = [];
            state.conversationsError = {
              status: payload?.status ?? null,
              errorCode: payload?.errorCode ?? null,
            };
          }
        }
      )
      .addCase(openWriterConversation.pending, (state, { meta }) => {
        state.isOpening = true;
        state.openRequestId = meta.requestId;
        state.isOpeningReading = false;
        state.readRequestId = null;
        state.readTargetId = null;
      })
      .addCase(openWriterConversation.fulfilled, (state, { meta, payload }) => {
        if (meta.requestId !== state.openRequestId) {
          return;
        }
        state.openRequestId = null;
        state.isOpening = false;
        if (!isSameSubject(writerSubjectOfRow(payload), state.subject)) {
          return;
        }
        state.conversationKey = payload.conversationKey;
        state.conversationId = payload.id;
        state.title = payload.title ?? null;
        state.promoteFromNew = false;
        state.exchanges = (payload.turns ?? []).map(storedExchange);
        state.grounding = groundingFromSelection(payload.sourceSelection);
        state.composerDocuments = chipsOf(payload.sourceSelection);
        state.composerCollections = collectionChipsOf(payload.sourceSelection);
        state.isUnavailable = false;
        state.isNaming = false;
        state.view = WriterView.CHAT;
        state.epoch += 1;
        state.requestGeneration += 1;
      })
      .addCase(openWriterConversation.rejected, (state, { meta }) => {
        if (meta.requestId === state.openRequestId) {
          state.isOpening = false;
          state.openRequestId = null;
        }
      })
      .addCase(readWriterConversation.pending, (state, { meta }) => {
        state.isOpeningReading = true;
        state.readRequestId = meta.requestId;
        state.readTargetId = meta.arg.id;
        state.isOpening = false;
        state.openRequestId = null;
      })
      .addCase(readWriterConversation.fulfilled, (state, { meta, payload }) => {
        if (meta.requestId !== state.readRequestId) {
          return;
        }
        state.isOpeningReading = false;
        state.readRequestId = null;
        state.readTargetId = null;
        state.reading = readingOf(payload.row, payload.detail);
        state.view = WriterView.READING;
      })
      .addCase(readWriterConversation.rejected, (state, { meta }) => {
        if (meta.requestId === state.readRequestId) {
          state.isOpeningReading = false;
          state.readRequestId = null;
        }
      })
      .addCase(renameWriterConversation.pending, (state, { meta }) => {
        const { conversationId, previousTitle } = meta.arg;
        const rename = state.renames[conversationId] ?? {
          savedTitle: previousTitle ?? null,
          pending: 0,
        };
        rename.latestRequestId = meta.requestId;
        rename.latestFailed = false;
        rename.pending += 1;
        state.renames[conversationId] = rename;
        showTitle(state, conversationId, meta.arg.title.trim());
      })
      .addCase(renameWriterConversation.fulfilled, (state, { meta }) => {
        const { conversationId } = meta.arg;
        const rename = state.renames[conversationId];
        if (!rename) {
          return;
        }
        rename.savedTitle = meta.arg.title.trim();
        if (rename.latestFailed) {
          showTitle(state, conversationId, rename.savedTitle);
        }
        settleRename(state, conversationId);
      })
      .addCase(renameWriterConversation.rejected, (state, { meta }) => {
        const { conversationId } = meta.arg;
        const rename = state.renames[conversationId];
        if (!rename) {
          return;
        }
        if (rename.latestRequestId === meta.requestId) {
          rename.latestFailed = true;
          showTitle(state, conversationId, rename.savedTitle);
        }
        settleRename(state, conversationId);
      })
      .addCase(deleteWriterConversation.fulfilled, (state, { payload }) => {
        state.conversations = state.conversations.filter(
          (conversation) => conversation.id !== payload.conversationId
        );
        if (state.readTargetId === payload.conversationId) {
          stopReading(state);
        }
        if (state.reading?.id === payload.conversationId) {
          stopReading(state);
          state.view = WriterView.HISTORY;
        }
        if (payload.replacesOpenChat) {
          startFresh(state, payload.nextConversationKey);
        }
      });
  },
});

export const {
  writerDocumentOpened,
  writerDocumentClosed,
  writerChatStarted,
  writerDocumentFirstSaved,
  writerRequestStarted,
  writerGroundingChanged,
  writerComposerChanged,
  writerRequestAbandoned,
  writerResultReceived,
  writerConversationRefused,
  writerExchangeCompleted,
  writerHistoryShown,
  writerChatShown,
  writerNamingStarted,
  writerNamingStopped,
  writerConversationNamed,
} = writerSlice.actions;

export default writerSlice.reducer;
